<template>
  <div class="new-unit-wrapper">
    <!-- 🌐 STICKY LANGUAGE TABS -->
    <v-card class="sticky-language-tabs" elevation="3">
      <div class="sticky-header-content">
        <!-- Language Tabs -->
        <div class="language-tabs-section">
          <v-tabs
            v-model="activeLang"
            density="comfortable"
            color="primary"
            class="language-tabs"
          >
            <v-tab
              v-for="lang in enabledLangs"
              :key="lang.code"
              :value="lang.code"
            >
              <v-icon class="mr-2">mdi-translate</v-icon>
              {{ lang.label }}
              <!-- Progress indicator -->
              <v-chip
                v-if="getCompletionPercentage(lang.code) >= 0"
                size="x-small"
                :color="
                  getCompletionPercentage(lang.code) === 100
                    ? 'success'
                    : getCompletionPercentage(lang.code) > 0
                    ? 'warning'
                    : 'grey'
                "
                class="ml-2"
              >
                {{ getCompletionPercentage(lang.code) }}%
              </v-chip>
            </v-tab>
          </v-tabs>
        </div>

        <!-- Action buttons -->
        <div class="action-buttons-section">
          <v-btn variant="text" size="small" @click="expandAll" class="mr-2">
            <v-icon class="mr-1">mdi-chevron-down</v-icon>
            {{ $t('units.expandAll') }}
          </v-btn>

          <v-btn variant="text" size="small" @click="collaspeAll" class="mr-2">
            <v-icon class="mr-1">mdi-chevron-up</v-icon>
            {{ $t('units.collapseAll') }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-container fluid class="new-unit-container">
      <v-flex xs12>
        <v-form class="mb-12" ref="addeditunitform">
          <v-expansion-panels v-model="openedPanel" multiple class="pa-0">
            <v-expansion-panel
              elevation="0"
              class="pt-8 pb-8 px-10 tj-elevation-1"
            >
              <v-expansion-panel-title class="expansion-panel-custom-title">
                <div class="font-weight-bold builder-title">
                  {{ $t('units.unitBuilder') }}
                </div>

                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <!-- Title -->
                <v-text-field
                  v-model="getLC(activeLang).title"
                  :placeholder="
                    $t('unitBuilder.enterTextHereWithLang', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  :label="
                    $t('unitBuilder.unitTitleLabel', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  :rules="[rules.required, rules.length(3)]"
                  hide-details="auto"
                  color="green"
                  variant="outlined"
                  class="mb-7 mt-4"
                  style="min-width: 100%"
                />
                <!-- Subtitle -->
                <v-text-field
                  v-model="getLC(activeLang).subtitle"
                  :placeholder="
                    $t('unitBuilder.enterTextHereWithLang', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  :label="
                    $t('unitBuilder.unitSubtitleLabel', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  hide-details="auto"
                  color="green"
                  variant="outlined"
                  class="mb-7"
                  style="min-width: 100%"
                />
                <!-- Description -->
                <v-text-field
                  v-model="getLC(activeLang).description"
                  :placeholder="$t('unitBuilder.enterDescriptionPlaceholder')"
                  :label="
                    $t('unitBuilder.unitDescriptionLabel', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  hide-details="auto"
                  color="green"
                  variant="outlined"
                  class="mb-7"
                  style="min-width: 100%"
                />
                <v-combobox
                  v-model="unitCategory"
                  :items="allCategories"
                  item-title="name"
                  :return-object="true"
                  :label="$t('unitBuilder.unitCategoryLabel')"
                  multiple
                  chips
                  variant="outlined"
                  color="#A0C548"
                  @update:model-value="updateCategories"
                  style="min-width: 100%"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              elevation="0"
              class="pt-8 pb-8 px-10 mt-4 tj-elevation-1"
            >
              <v-expansion-panel-title class="expansion-panel-custom-title">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.assignments') }}
                </div>

                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div>
                  {{ $t('unitBuilder.instructionalTextAssignments') }}
                </div>
                <!-- LISTEN -->
                <v-text-field
                  v-model="getLC(activeLang).listenLabel"
                  :label="
                    $t('unitBuilder.actionLabel', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  :placeholder="$t('unitBuilder.exampleListen')"
                  variant="underlined"
                  hide-details="auto"
                  persistent-placeholder=""
                  color="green"
                  class="mt-5 full-opactiy-label"
                  style="min-width: 100%"
                ></v-text-field>
                <CustomQuill
                  :key="`assign-listen-${activeLang}`"
                  :id="`assignment-editor-listen-${activeLang}`"
                  class="editor-2 mb-6 sml-editor"
                  :model-value="getLC(activeLang).listen || ''"
                  @update:modelValue="
                    (v) => (getLC(activeLang).listen = v || '')
                  "
                  :placeholder="
                    $t('unitBuilder.enterDescriptionHereWithLang', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  @ready="onEditorReady('listenEditor')"
                />
                <!-- COMPLETE -->
                <v-text-field
                  v-model="getLC(activeLang).completeLabel"
                  :label="
                    $t('unitBuilder.actionLabel', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  :placeholder="$t('unitBuilder.exampleComplete')"
                  variant="underlined"
                  hide-details="auto"
                  persistent-placeholder=""
                  color="green"
                  class="mt-5 full-opactiy-label"
                  style="min-width: 100%"
                ></v-text-field>
                <CustomQuill
                  :key="`assign-complete-${activeLang}`"
                  :id="`assignment-editor-complete-${activeLang}`"
                  class="editor-3 mb-8 sml-editor"
                  :model-value="getLC(activeLang).complete || ''"
                  @update:modelValue="
                    (v) => (getLC(activeLang).complete = v || '')
                  "
                  :placeholder="
                    $t('unitBuilder.enterDescriptionHereWithLang', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                />
                <!-- CONTINUE -->
                <v-text-field
                  v-model="getLC(activeLang).continueLearningLabel"
                  :label="
                    $t('unitBuilder.actionLabel', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                  :placeholder="$t('unitBuilder.exampleContinue')"
                  variant="underlined"
                  hide-details="auto"
                  persistent-placeholder=""
                  color="green"
                  class="mt-5 full-opactiy-label"
                  style="min-width: 100%"
                ></v-text-field>
                <CustomQuill
                  :key="`assign-continue-${activeLang}`"
                  :id="`assignment-editor-continue-${activeLang}`"
                  class="editor-4 mb-8 sml-editor"
                  :model-value="getLC(activeLang).continueLearning || ''"
                  @update:modelValue="
                    (v) => (getLC(activeLang).continueLearning = v || '')
                  "
                  :placeholder="
                    $t('unitBuilder.enterDescriptionHereWithLang', {
                      lang: activeLang.toUpperCase(),
                    })
                  "
                />
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title
                class="expansion-panel-custom-title pa-0"
              >
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.truth') }}
                </div>

                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>

              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedTruthPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel
                    elevation="0"
                    class="pt-8 pb-8 px-10 tj-elevation-1"
                  >
                    <v-expansion-panel-title
                      class="expansion-panel-custom-title"
                    >
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.versesAndQuotes') }}
                      </div>

                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextVersesQuotes') }}
                      </div>
                      <VersesQuotes
                        :backdoor="backdoor"
                        :section="`truthVersesQuotes_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).truthVersesQuotes"
                        @allversequotes="versesQuotesChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel
                    elevation="0"
                    class="pt-8 pb-8 px-10 tj-elevation-1"
                  >
                    <v-expansion-panel-title
                      class="expansion-panel-custom-title"
                    >
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.inBible') }}
                      </div>

                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextInBible') }}
                      </div>
                      <CustomQuill
                        :key="`truthInBible-${activeLang}`"
                        :id="`editor-truthInBible-${activeLang}`"
                        class="editor-5 mb-8"
                        :model-value="getLC(activeLang).truthInBible || ''"
                        @update:modelValue="
                          (v) => (getLC(activeLang).truthInBible = v || '')
                        "
                        :placeholder="
                          $t('unitBuilder.enterDescriptionHereWithLang', {
                            lang: activeLang.toUpperCase(),
                          })
                        "
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.studyQuestionsSectionTitle') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextStudyQuestionsTruth') }}
                      </div>
                      <StudyQuestions
                        :backdoor="backdoor"
                        :section="`truthStudyQuestions_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).truthStudyQuestions"
                        @allstudyquestions="studyQuestionsChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.bodyContent') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextBodyContentTruth') }}
                      </div>
                      <CustomQuill
                        :key="`truthBody-${activeLang}`"
                        :id="`editor-truthBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="getLC(activeLang).truthBodyContent || ''"
                        @update:modelValue="
                          (v) => (getLC(activeLang).truthBodyContent = v || '')
                        "
                        :placeholder="
                          $t('unitBuilder.enterDescriptionHereWithLang', {
                            lang: activeLang.toUpperCase(),
                          })
                        "
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.equipping') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedEquipPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.bodyContent') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextBodyContentEquipping') }}
                      </div>
                      <CustomQuill
                        :key="`equipBody-${activeLang}`"
                        :id="`editor-equipBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="getLC(activeLang).equipBodyContent || ''"
                        @update:modelValue="
                          (v) => (getLC(activeLang).equipBodyContent = v || '')
                        "
                        :placeholder="
                          $t('unitBuilder.enterDescriptionHereWithLang', {
                            lang: activeLang.toUpperCase(),
                          })
                        "
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.studyQuestionsSectionTitle') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextStudyQuestionsEquipping') }}
                      </div>
                      <StudyQuestions
                        :backdoor="backdoor"
                        :section="`equipStudyQuestions_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).equipStudyQuestions"
                        @allstudyquestions="studyQuestionsChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.accountability') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedAccountPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.bodyContent') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextBodyContentAccountability') }}
                      </div>
                      <CustomQuill
                        :key="`accountBody-${activeLang}`"
                        :id="`editor-accountBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="
                          getLC(activeLang).accountBodyContent || ''
                        "
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).accountBodyContent = v || '')
                        "
                        :placeholder="
                          $t('unitBuilder.enterDescriptionHereWithLang', {
                            lang: activeLang.toUpperCase(),
                          })
                        "
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>

                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-title>
                      <div class="font-weight-bold text-h6">
                        {{ $t('unitBuilder.studyQuestionsSectionTitle') }}
                      </div>
                      <template #actions="{ expanded }">
                        <v-icon
                          class="transition-transform"
                          :class="{ 'rotate-180': expanded }"
                        >
                          arrowdown
                        </v-icon>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <div>
                        {{ $t('unitBuilder.instructionalTextStudyQuestionsAccountability') }}
                      </div>
                      <StudyQuestions
                        :backdoor="backdoor"
                        :section="`accountStudyQuestions_${activeLang}`"
                        :currentLang="activeLang"
                        :data="getLC(activeLang).accountStudyQuestions"
                        @allstudyquestions="studyQuestionsChild"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.footnotes') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedFootnotesPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-text>
                      <CustomQuill
                        :key="`footnotesBody-${activeLang}`"
                        :id="`editor-footnotesBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="
                          getLC(activeLang).footnotesBodyContent || ''
                        "
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).footnotesBodyContent = v || '')
                        "
                        :placeholder="
                          $t('unitBuilder.enterContentHereWithLang', {
                            lang: activeLang.toUpperCase(),
                          })
                        "
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel
              class="pt-8 pb-8"
              elevation="0"
              style="background: transparent"
            >
              <v-expansion-panel-title class="pa-0">
                <div class="font-weight-bold text-h6">
                  {{ $t('units.sections.copyright') }}
                </div>
                <template #actions="{ expanded }">
                  <v-icon
                    class="transition-transform"
                    :class="{ 'rotate-180': expanded }"
                  >
                    arrowdown
                  </v-icon>
                </template>
              </v-expansion-panel-title>
              <v-divider class="dashed" style="margin-top: 15px"></v-divider>
              <v-expansion-panel-text class="sub-panel mt-5">
                <v-expansion-panels
                  v-model="openedCopyrightPanels"
                  multiple
                  class="pa-0"
                >
                  <v-expansion-panel class="pt-8 pb-8 px-10 tj-elevation-1">
                    <v-expansion-panel-text>
                      <CustomQuill
                        :key="`copyrightBody-${activeLang}`"
                        :id="`editor-copyrightBody-${activeLang}`"
                        class="editor-4 mb-8 mt-8"
                        :model-value="
                          getLC(activeLang).copyrightBodyContent || ''
                        "
                        @update:modelValue="
                          (v) =>
                            (getLC(activeLang).copyrightBodyContent = v || '')
                        "
                        :placeholder="
                          $t('unitBuilder.enterContentHereWithLang', {
                            lang: activeLang.toUpperCase(),
                          })
                        "
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-progress-linear
            v-if="isAddingUnit"
            :indeterminate="isAddingUnit"
            :active="isAddingUnit"
            color="#592892"
            class="mb-5"
          ></v-progress-linear>
          <div class="text-center">
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold mb-14 px-14"
              :loading="isAddingUnit"
              :disabled="isAddingUnit"
              @click="addUnit('draft')"
              >{{ $t('units.saveAsDraft') }}
              <template #loader>
                <span>{{ $t('unitBuilder.saving') }}</span>
                <span class="custom-loader">
                  <v-icon>cached</v-icon>
                </span>
              </template>
            </v-btn>
            <v-btn
              color="#592892"
              class="elevation-0 font-weight-bold ml-7 mb-14 px-14"
              :loading="isAddingUnit"
              :disabled="isAddingUnit"
              @click="addUnit('published')"
              >{{ $t('units.publish') }}
              <template #loader>
                <span>{{ $t('unitBuilder.saving') }}</span>
                <span class="custom-loader">
                  <v-icon>cached</v-icon>
                </span>
              </template>
            </v-btn>
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold ml-7 mb-14 px-14"
              :loading="isPreviewingUnit"
              :disabled="isPreviewingUnit"
              @click="previewUnit(unit)"
              >{{ $t('units.previewUnit') }}
              <template #loader>
                <span>{{ $t('unitBuilder.loading') }}</span>
                <span class="custom-loader">
                  <v-icon>cached</v-icon>
                </span>
              </template>
            </v-btn>
          </div>
          <v-alert
            v-if="feedback"
            border="start"
            color="#592892"
            class="text-center"
          >
            {{ feedback }}
          </v-alert>
          <v-divider class="dashed"></v-divider>
        </v-form>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import { mapGetters } from 'vuex'
  import StudyQuestions from '@/components/utilities/StudyQuestions'
  import VersesQuotes from '@/components/utilities/VersesQuotes'
  import CustomQuill from '@/components/utilities/CustomQuill.vue'
  import { openPreview } from '@/helpers/previewBridge'

  export default {
    name: 'NewUnit',
    props: ['allCategories'],
    components: {
      StudyQuestions,
      VersesQuotes,
      CustomQuill,
    },
    data() {
      return {
        feedback: null,
        openedPanel: [0, 1, 2, 3, 4, 5, 6],
        openedTruthPanels: [0, 1, 2, 3],
        openedEquipPanels: [0, 1],
        openedAccountPanels: [0, 1],
        openedFootnotesPanels: [0],
        openedCopyrightPanels: [0],
        isAddingUnit: false,
        isPreviewingUnit: false,
        completionScore: 0,
        backdoor: 0,
        // Language support
        activeLang: 'en',
        localizedContent: {
          en: {
            truthVersesQuotes: [],
            truthStudyQuestions: [],
            equipStudyQuestions: [],
            accountStudyQuestions: [],
          },
          pt: {
            truthVersesQuotes: [],
            truthStudyQuestions: [],
            equipStudyQuestions: [],
            accountStudyQuestions: [],
          },
        },
        title: '',
        subtitle: '',
        description: '',
        unitCategory: [],
        newCategory: [],
        listen: '',
        listenLabel: 'Listen',
        complete: '',
        completeLabel: 'Complete',
        continueLearning: '',
        continueLearningLabel: 'Continue',
        truthBodyContent: '',
        truthInBible: '',
        equipBodyContent: '',
        accountBodyContent: '',
        footnotesBodyContent: '',
        copyrightBodyContent: '',
        unit: [],
      }
    },
    computed: {
      ...mapGetters('UserData', ['isAuthenticated']),
      ...mapGetters('AppData', ['prevUnit', 'cid']),
      enabledLangs() {
        return [
          { code: 'en', label: 'English' },
          { code: 'pt', label: 'Português' },
        ]
      },
      rules() {
        return {
          length: (len) => (v) =>
            (v || '').length >= len ||
            this.$t('unitBuilder.minCharactersRequired', { len }),
          required: (v) => !!v || this.$t('unitBuilder.fieldRequired'),
        }
      },
    },
    methods: {
      getLC(lang) {
        if (!this.localizedContent[lang]) {
          this.localizedContent[lang] = {
            truthVersesQuotes: [],
            truthStudyQuestions: [],
            equipStudyQuestions: [],
            accountStudyQuestions: [],
          }
        }
        return this.localizedContent[lang]
      },
      getCompletionPercentage(lang) {
        const content = this.getLC(lang)
        const fields = [
          'title',
          'subtitle',
          'description',
          'listen',
          'complete',
          'continueLearning',
          'truthBodyContent',
          'truthInBible',
          'equipBodyContent',
          'accountBodyContent',
          'footnotesBodyContent',
          'copyrightBodyContent',
        ]

        let filledFields = 0
        fields.forEach((field) => {
          if (content[field] && content[field].trim() !== '') {
            filledFields++
          }
        })

        // Add verses and questions to completion calculation
        if (
          content.truthVersesQuotes &&
          Array.isArray(content.truthVersesQuotes) &&
          content.truthVersesQuotes.length > 0
        )
          filledFields++
        if (
          content.truthStudyQuestions &&
          Array.isArray(content.truthStudyQuestions) &&
          content.truthStudyQuestions.length > 0
        )
          filledFields++
        if (
          content.equipStudyQuestions &&
          Array.isArray(content.equipStudyQuestions) &&
          content.equipStudyQuestions.length > 0
        )
          filledFields++
        if (
          content.accountStudyQuestions &&
          Array.isArray(content.accountStudyQuestions) &&
          content.accountStudyQuestions.length > 0
        )
          filledFields++

        const totalFields = fields.length + 4 // Add 4 for verses/questions
        const percentage = Math.round((filledFields / totalFields) * 100)
        console.log(
          `Completion for ${lang}:`,
          percentage,
          'filledFields:',
          filledFields,
          'totalFields:',
          totalFields
        )
        return percentage
      },
      closeBuilder() {
        // Emit event to parent to close the form
        this.$emit('closeUnitsForm', { closeUnitsForm: true })
      },
      onEditorReady(refName) {
        this.$nextTick(() => {
          const editor = this.$refs[refName]?.getEditor?.()
          if (editor) {
            // console.log(`✅ ${refName} initialized`, editor)
          }
        })
      },
      addUnit(status) {
        this.feedback = ''

        // Check if we have a title in any language
        const hasTitle =
          this.title ||
          (this.getLC('en').title && this.getLC('en').title.trim()) ||
          (this.getLC('pt').title && this.getLC('pt').title.trim())

        if (hasTitle) {
          this.isAddingUnit = true

          // filter out any new categories without IDs
          const updatedUnitCategory = this.unitCategory.filter(
            (item) => item.id !== undefined
          )

          // if there was a new category add it to the collection
          if (this.newCategory.length) {
            for (let index = 0; index < this.newCategory.length; index++) {
              const cat = this.newCategory[index]
              db.collection('categories')
                .add({
                  name: cat,
                })
                .then((docRef) => {
                  // add new categories to the unit category array
                  updatedUnitCategory.push({
                    id: docRef.id,
                  })
                })
            }
          }
          // remove the name property from the section category array before adding it to the collection
          updatedUnitCategory.map(function (item) {
            delete item.name
            return item
          })

          // Lets debouce this
          if (this.timeout) clearTimeout(this.timeout)

          this.timeout = setTimeout(() => {
            let churchPin = !this.checkAuthorization(['superAdmin'])
              ? this.cid
              : null
            // console.log('churchPin ', churchPin, this.cid)

            // Get verses and questions from localizedContent for English (primary language)
            let truthQues =
              this.getLC('en').truthStudyQuestions &&
              Array.isArray(this.getLC('en').truthStudyQuestions) &&
              this.getLC('en').truthStudyQuestions.length > 0
                ? this.getLC('en').truthStudyQuestions
                : []
            let equipQues =
              this.getLC('en').equipStudyQuestions &&
              Array.isArray(this.getLC('en').equipStudyQuestions) &&
              this.getLC('en').equipStudyQuestions.length > 0
                ? this.getLC('en').equipStudyQuestions
                : []
            let accountQues =
              this.getLC('en').accountStudyQuestions &&
              Array.isArray(this.getLC('en').accountStudyQuestions) &&
              this.getLC('en').accountStudyQuestions.length > 0
                ? this.getLC('en').accountStudyQuestions
                : []
            let truthVrsQts =
              this.getLC('en').truthVersesQuotes &&
              Array.isArray(this.getLC('en').truthVersesQuotes) &&
              this.getLC('en').truthVersesQuotes.length > 0
                ? this.getLC('en').truthVersesQuotes
                : []

            // This is used for caluclating a completion score
            if (this.title) this.completionScore++
            if (this.subtitle) this.completionScore++
            if (this.listen) this.completionScore++
            if (this.complete) this.completionScore++
            if (this.continueLearning) this.completionScore++
            if (truthVrsQts && truthVrsQts.length) this.completionScore++
            if (truthQues && truthQues.length) this.completionScore++
            if (this.truthBodyContent) this.completionScore++
            if (this.truthInBible) this.completionScore++
            if (equipQues && equipQues.length) this.completionScore++
            if (this.equipBodyContent) this.completionScore++
            if (this.accountBodyContent) this.completionScore++
            if (accountQues && accountQues.length) this.completionScore++

            let type = this.checkAuthorization(['superAdmin'])
              ? 'public'
              : 'private'
            let verified = 'verified'

            // your action
            db.collection('units')
              .add({
                cid: churchPin,
                // Keep top-level fields for backward compatibility
                title: this.getLC('en').title || this.title,
                subtitle: this.getLC('en').subtitle || this.subtitle,
                description: this.getLC('en').description || this.description,
                category: updatedUnitCategory,
                listen: this.getLC('en').listen || this.listen,
                listenLabel: this.getLC('en').listenLabel || this.listenLabel,
                complete: this.getLC('en').complete || this.complete,
                completeLabel:
                  this.getLC('en').completeLabel || this.completeLabel,
                continueLearning:
                  this.getLC('en').continueLearning || this.continueLearning,
                continueLearningLabel:
                  this.getLC('en').continueLearningLabel ||
                  this.continueLearningLabel,
                truthVersesQuotes: truthVrsQts,
                truthStudyQuestions: truthQues,
                truthBodyContent:
                  this.getLC('en').truthBodyContent || this.truthBodyContent,
                truthInBible:
                  this.getLC('en').truthInBible || this.truthInBible,
                equipStudyQuestions: equipQues,
                equipBodyContent:
                  this.getLC('en').equipBodyContent || this.equipBodyContent,
                accountBodyContent:
                  this.getLC('en').accountBodyContent ||
                  this.accountBodyContent,
                accountStudyQuestions: accountQues,
                footnotesBodyContent:
                  this.getLC('en').footnotesBodyContent ||
                  this.footnotesBodyContent,
                copyrightBodyContent:
                  this.getLC('en').copyrightBodyContent ||
                  this.copyrightBodyContent,
                completionScore: this.completionScore,
                status: status,
                type: type,
                verified: verified,
                // Add localized content structure (ensure all arrays are initialized)
                localizedContent: {
                  en: {
                    ...this.getLC('en'),
                    truthVersesQuotes: this.getLC('en').truthVersesQuotes || [],
                    truthStudyQuestions:
                      this.getLC('en').truthStudyQuestions || [],
                    equipStudyQuestions:
                      this.getLC('en').equipStudyQuestions || [],
                    accountStudyQuestions:
                      this.getLC('en').accountStudyQuestions || [],
                  },
                  pt: {
                    ...this.getLC('pt'),
                    truthVersesQuotes: this.getLC('pt').truthVersesQuotes || [],
                    truthStudyQuestions:
                      this.getLC('pt').truthStudyQuestions || [],
                    equipStudyQuestions:
                      this.getLC('pt').equipStudyQuestions || [],
                    accountStudyQuestions:
                      this.getLC('pt').accountStudyQuestions || [],
                  },
                },
              })
              .then(() => {
                // trigger the components to reset
                this.backdoor++

                // this.openUnitsForm = false
                this.$emit('closeUnitsForm', {
                  closeUnitsForm: true,
                  title: this.title,
                })

                this.isAddingUnit = false

                // Reset fields
                this.reset()
              })
          }, 300)
        } else {
          this.feedback = this.$t('unitBuilder.needUnitTitleInOneLanguage')
        }
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const hasRole = permissions.filter(() =>
            userRoles.some((role) => permissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      collaspeAll() {
        this.openedPanel = []
        this.openedTruthPanels = []
        this.openedEquipPanels = []
        this.openedAccountPanels = []
        this.openedFootnotesPanels = []
        this.openedCopyrightPanels = []
      },
      expandAll() {
        this.openedPanel = [0, 1, 2, 3, 4, 5, 6]
        this.openedTruthPanels = [0, 1, 2, 3]
        this.openedEquipPanels = [0, 1]
        this.openedAccountPanels = [0, 1]
        // Force expansion by clearing first, then setting
        this.openedFootnotesPanels = []
        this.openedCopyrightPanels = []
        this.$nextTick(() => {
          this.openedFootnotesPanels = [0]
          this.openedCopyrightPanels = [0]
        })
      },
      previewUnit() {
        // Get verses and questions from localizedContent for the currently selected language
        let truthQues =
          this.getLC(this.activeLang).truthStudyQuestions &&
          Array.isArray(this.getLC(this.activeLang).truthStudyQuestions) &&
          this.getLC(this.activeLang).truthStudyQuestions.length > 0
            ? this.getLC(this.activeLang).truthStudyQuestions
            : []
        let equipQues =
          this.getLC(this.activeLang).equipStudyQuestions &&
          Array.isArray(this.getLC(this.activeLang).equipStudyQuestions) &&
          this.getLC(this.activeLang).equipStudyQuestions.length > 0
            ? this.getLC(this.activeLang).equipStudyQuestions
            : []
        let accountQues =
          this.getLC(this.activeLang).accountStudyQuestions &&
          Array.isArray(this.getLC(this.activeLang).accountStudyQuestions) &&
          this.getLC(this.activeLang).accountStudyQuestions.length > 0
            ? this.getLC(this.activeLang).accountStudyQuestions
            : []
        let truthVrsQts =
          this.getLC(this.activeLang).truthVersesQuotes &&
          Array.isArray(this.getLC(this.activeLang).truthVersesQuotes) &&
          this.getLC(this.activeLang).truthVersesQuotes.length > 0
            ? this.getLC(this.activeLang).truthVersesQuotes
            : []

        // build array to pass to Preview
        const previewUnit = {
          title: this.getLC(this.activeLang).title || this.title,
          subtitle: this.getLC(this.activeLang).subtitle || this.subtitle,
          listen: this.getLC(this.activeLang).listen || this.listen,
          listenLabel:
            this.getLC(this.activeLang).listenLabel || this.listenLabel,
          complete: this.getLC(this.activeLang).complete || this.complete,
          completeLabel:
            this.getLC(this.activeLang).completeLabel || this.completeLabel,
          continueLearning:
            this.getLC(this.activeLang).continueLearning ||
            this.continueLearning,
          continueLearningLabel:
            this.getLC(this.activeLang).continueLearningLabel ||
            this.continueLearningLabel,
          truthVersesQuotes: truthVrsQts,
          truthStudyQuestions: truthQues,
          truthBodyContent:
            this.getLC(this.activeLang).truthBodyContent ||
            this.truthBodyContent,
          truthInBible:
            this.getLC(this.activeLang).truthInBible || this.truthInBible,
          equipStudyQuestions: equipQues,
          equipBodyContent:
            this.getLC(this.activeLang).equipBodyContent ||
            this.equipBodyContent,
          accountBodyContent:
            this.getLC(this.activeLang).accountBodyContent ||
            this.accountBodyContent,
          accountStudyQuestions: accountQues,
          footnotesBodyContent:
            this.getLC(this.activeLang).footnotesBodyContent ||
            this.footnotesBodyContent,
          copyrightBodyContent:
            this.getLC(this.activeLang).copyrightBodyContent ||
            this.copyrightBodyContent,
          localizedContent: this.localizedContent,
        }

        const opened = openPreview(this.$router, this.$route, {
          name: 'PreviewUnit',
          lang: this.activeLang || 'en',
          payload: previewUnit,
        })
        if (!opened) {
          this.feedback =
            'Preview could not be opened. Free some browser storage and try again.'
        }
      },
      reset() {
        this.title = ''
        this.subtitle = ''
        this.category = ''
        this.unitCategory = []
        this.listen = ''
        this.listenLabel = ''
        this.complete = ''
        this.completeLabel = ''
        this.continueLearning = ''
        this.continueLearningLabel = ''
        this.truthBodyContent = ''
        this.truthInBible = ''
        this.equipBodyContent = ''
        this.accountBodyContent = ''
        this.footnotesBodyContent = ''
        this.copyrightBodyContent = ''
        // Reset localized content
        this.localizedContent = {
          en: {
            truthVersesQuotes: [],
            truthStudyQuestions: [],
            equipStudyQuestions: [],
            accountStudyQuestions: [],
          },
          pt: {
            truthVersesQuotes: [],
            truthStudyQuestions: [],
            equipStudyQuestions: [],
            accountStudyQuestions: [],
          },
        }
        this.activeLang = 'en'
      },
      studyQuestionsChild(value) {
        // Extract language from section name (e.g., "truthStudyQuestions_en" -> "en")
        const lang = value.section.split('_').pop()
        const sectionName = value.section.replace(`_${lang}`, '')

        // Update the localized content
        if (sectionName === 'truthStudyQuestions') {
          this.localizedContent[lang].truthStudyQuestions =
            value.questions && value.questions.length ? value.questions : []
        } else if (sectionName === 'equipStudyQuestions') {
          this.localizedContent[lang].equipStudyQuestions =
            value.questions && value.questions.length ? value.questions : []
        } else if (sectionName === 'accountStudyQuestions') {
          this.localizedContent[lang].accountStudyQuestions =
            value.questions && value.questions.length ? value.questions : []
        }
      },
      versesQuotesChild(value) {
        // Extract language from section name (e.g., "truthVersesQuotes_en" -> "en")
        const lang = value.section.split('_').pop()
        const sectionName = value.section.replace(`_${lang}`, '')

        // Update the localized content
        if (sectionName === 'truthVersesQuotes') {
          this.localizedContent[lang].truthVersesQuotes =
            value.versesquotes && value.versesquotes.length
              ? value.versesquotes
              : []
        }
      },
      updateCategories() {
        const allCat = this.allCategories
        const unitCat = this.unitCategory

        // Find values that are in unitCat but not in allCat
        this.newCategory = unitCat.filter(function (obj) {
          return !allCat.some(function (obj2) {
            return obj.name == obj2.name
          })
        })
      },
    },
  }
</script>

<style>
  .sml-editor .ql-editor {
    min-height: 100px;
    max-height: 150px;
  }
  .custom-label {
    transform: scale(0.75);
    display: inline-flex;
  }

  /* Sticky language tabs */
  .sticky-language-tabs {
    position: -webkit-sticky; /* Safari */
    position: sticky;
    top: 100px;
    z-index: 1000;
    background: white;
    border-bottom: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    margin-bottom: 16px;
  }

  /* Sticky header content layout */
  .sticky-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    min-height: 64px;
  }

  /* Language tabs section */
  .language-tabs-section {
    flex: 1;
    min-width: 300px;
  }

  /* Action buttons section */
  .action-buttons-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Language tabs styling */
  .language-tabs {
    min-width: 300px;
  }

  .language-tabs .v-tab {
    min-width: 120px;
  }

  /* Wrapper for sticky positioning */
  .new-unit-wrapper {
    position: relative;
  }

  /* Dark mode support */
  .v-theme--dark .sticky-language-tabs {
    background: #1e1e1e;
    border-bottom-color: #424242;
  }

  /* Remove old panel actions styles since we moved them to sticky header */
  .new-unit-container .panel-actions {
    display: none;
  }

  /* Remove left padding from expansion panel titles only */
  .v-expansion-panel-title {
    padding-left: 0 !important;
  }
</style>
